import React from "react";
import { Clock, MapPin, Phone } from "lucide-react";
import Blocks, { Icon } from "./Blocks";
import Reveal from "./Reveal";
import { useBooking } from "./BookingDialog";
import { ModelGrid } from "./Models";

/**
 * Renders one complete venue (all of its sections + contact) as part of the
 * single long guide page, matching the source document's structure.
 */
const VenueBlock = ({ venue, index }) => {
  const { open } = useBooking();

  const hasRooms = venue.sections.some((s) => s.id === "rooms");
  const modelsBefore = hasRooms ? "rooms" : null;
  const modelsAfter = hasRooms ? null : "introduction";

  const ModelsSection = () => (
    <div id={`${venue.slug}-models`} className="scroll-mt-32 pt-16">
      <Reveal>
        <span className="eyebrow mb-4">Models</span>
        <h3 className="wiki-h2">The Ladies of {venue.name}</h3>
        <div className="section-divider ml-0 mt-5" />
      </Reveal>
      <ModelGrid venue={venue} />
    </div>
  );

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id={venue.slug}
      className={`relative scroll-mt-28 py-20 sm:py-24 ${venue.theme.bg}`}
    >
      <div className="hairline absolute inset-x-0 top-0" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* venue title */}
        <Reveal>
          <span
            className={`inline-flex rounded-full px-4 py-2 text-xs font-semibold ${venue.theme.chip}`}
          >
            {venue.badge}
          </span>
          <h2
            data-testid={`venue-title-${venue.slug}`}
            className={`mt-6 border-b pb-4 font-serif text-5xl leading-[1.05] tracking-tight text-foreground sm:text-6xl lg:text-7xl ${venue.theme.rule}`}
          >
            {venue.name}
          </h2>
          <p className={`mt-4 text-sm uppercase tracking-[0.28em] ${venue.theme.accent}`}>
            {venue.suburb} &middot; {venue.established}
          </p>
        </Reveal>

        {/* gallery, straight under the title */}
        {venue.gallery?.length ? (
          <Reveal delay={60}>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {venue.gallery.map((g) => (
                <figure
                  key={g.img}
                  data-testid={`venue-gallery-item-${venue.slug}`}
                  className={`overflow-hidden rounded-xl border bg-background/70 ${venue.theme.rule}`}
                >
                  <div className="aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={g.img}
                      alt={g.caption}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                  <figcaption className="px-5 py-4 text-sm leading-relaxed text-muted-foreground">
                    {g.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
        ) : null}

        {/* venue sub navigation, as listed in the document */}
        <Reveal delay={80}>
          <div className="mt-8 flex flex-wrap gap-2">
            {venue.nav.map((n) => (
              <button
                key={n.id}
                onClick={() => go(`${venue.slug}-${n.id}`)}
                className="rounded-full bg-muted/60 px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                {n.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={140}>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {venue.highlights.map((h) => (
              <div
                key={h.label}
                className="flex items-center gap-3 rounded-xl border border-border/60 bg-background px-5 py-4"
              >
                <Icon name={h.icon} className="h-4 w-4 text-primary" />
                <span className="text-sm text-foreground/85">{h.label}</span>
              </div>
            ))}
          </div>
        </Reveal>

        {/* every section of this venue */}
        {venue.sections.map((section) => (
          <React.Fragment key={section.id}>
            {section.id === modelsBefore && <ModelsSection />}

            <div
              id={`${venue.slug}-${section.id}`}
              className="scroll-mt-32 pt-16"
            >
              <Reveal>
                <span className="eyebrow mb-4">{section.eyebrow}</span>
                <h3 className="wiki-h2">{section.title}</h3>
                <div className="section-divider ml-0 mt-5" />
              </Reveal>
              <div className="mt-9">
                <Blocks blocks={section.blocks} venueName={venue.name} />
              </div>
            </div>

            {section.id === modelsAfter && <ModelsSection />}
          </React.Fragment>
        ))}

        {/* contact */}
        <div id={`${venue.slug}-contact`} className="scroll-mt-32 pt-16">
          <Reveal>
            <span className="eyebrow mb-4">Contact</span>
            <h3 className="wiki-h2">Visit {venue.name}</h3>
            <div className="section-divider ml-0 mt-5" />
          </Reveal>

          <div className="mt-9 grid gap-8 lg:grid-cols-[1fr_1.3fr]">
            <Reveal>
              <div className="luxury-card space-y-7 p-8">
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <MapPin className="h-4 w-4 text-primary" />
                  </span>
                  <div>
                    <p className="text-xs tracking-wide text-muted-foreground">Address:</p>
                    <p className="mt-1 text-foreground">{venue.contact.address}</p>
                  </div>
                </div>
                {venue.contact.phone ? (
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Phone className="h-4 w-4 text-primary" />
                  </span>
                  <div>
                    <p className="text-xs tracking-wide text-muted-foreground">Phone:</p>
                    <a
                      href={`tel:${venue.contact.phone.replace(/[^0-9+]/g, "")}`}
                      className="mt-1 block text-foreground transition-colors hover:text-primary"
                    >
                      {venue.contact.phone}
                    </a>
                  </div>
                </div>
                ) : null}
                {venue.contact.hours ? (
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Clock className="h-4 w-4 text-primary" />
                  </span>
                  <div>
                    <p className="text-xs tracking-wide text-muted-foreground">Hours:</p>
                    <p className="mt-1 text-foreground">{venue.contact.hours}</p>
                  </div>
                </div>
                ) : null}
                <button
                  onClick={() => open({ venue: venue.name })}
                  className="btn-pink-glow w-full"
                >
                  Make an Enquiry
                </button>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  venue.contact.address
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`venue-map-${venue.slug}`}
                className="group block h-full min-h-[320px] overflow-hidden rounded-xl border border-border/60"
              >
                <img
                  src={`/media/maps/${venue.slug}.jpg`}
                  alt={`Map of ${venue.name}, ${venue.contact.address}`}
                  loading="lazy"
                  className="h-full min-h-[320px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VenueBlock;
